import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { authSession, changeOwnPassword } from "../../../services/auth";

/** Same rules as the API: 10 characters, a letter, a digit and a special character. */
export const passwordRules = (value) => [
  { key: "length", ok: value.length >= 10, label: "At least 10 characters" },
  { key: "mix", ok: /[a-zA-Z]/.test(value) && /\d/.test(value), label: "A letter and a digit" },
  { key: "special", ok: /[^a-zA-Z0-9]/.test(value), label: "A special character (e.g. ! ? @ # -)" },
];

/**
 * Change of the logged-in admin's password. `temporary`: signed in with a
 * temporary password (new account, reset): changing it is advised, never
 * required. Every other session is closed by the API; this one stays open.
 */
const ChangePasswordCard = ({ temporary, onChanged }) => {
  const { t } = useTranslation();
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [show, setShow] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState({ field: "", message: "" });

  const rules = passwordRules(next);
  const valid = rules.every((rule) => rule.ok) && next === confirm && current;

  const submit = async (event) => {
    event.preventDefault();
    if (!valid) return;
    setSaving(true);
    setError({ field: "", message: "" });
    try {
      await changeOwnPassword(current, next);
      await authSession();
      toast.success(t("Password changed. Your other sessions have been closed."));
      setCurrent("");
      setNext("");
      setConfirm("");
      onChanged?.();
    } catch (err) {
      const data = err?.response?.data || {};
      setError({ field: data.field || "", message: data.message || t("Something went wrong") });
    } finally {
      setSaving(false);
    }
  };

  const type = show ? "text" : "password";
  return (
    <section className="ui-card profile-password" aria-labelledby="password-title">
      <h2 id="password-title">{t("Change my password")}</h2>
      <p className="ui-hint">
        {temporary
          ? t("You are using a temporary password received by email. We advise you to choose your own.")
          : t("Changing your password signs you out of every other device.")}
      </p>
      <form onSubmit={submit} className="profile-password-form">
        <div className="ui-field">
          <label htmlFor="pw-current">{temporary ? t("Temporary password (received by email)") : t("Current password")}</label>
          <input id="pw-current" className="ui-input" type={type} autoComplete="current-password" value={current} onChange={(e) => setCurrent(e.target.value)} />
          {error.field === "currentPassword" && <p className="ui-hint" data-error="">{error.message}</p>}
        </div>
        <div className="ui-field">
          <label htmlFor="pw-new">{t("New password")}</label>
          <input id="pw-new" className="ui-input" type={type} autoComplete="new-password" value={next} onChange={(e) => setNext(e.target.value)} />
          <ul className="profile-password-rules">
            {rules.map((rule) => (
              <li key={rule.key} data-ok={rule.ok || undefined}>
                <i className={rule.ok ? "ri-checkbox-circle-fill" : "ri-checkbox-blank-circle-line"} aria-hidden="true"></i>
                {t(rule.label)}
              </li>
            ))}
          </ul>
          {error.field === "newPassword" && <p className="ui-hint" data-error="">{error.message}</p>}
        </div>
        <div className="ui-field">
          <label htmlFor="pw-confirm">{t("Confirm the new password")}</label>
          <input id="pw-confirm" className="ui-input" type={type} autoComplete="new-password" value={confirm} onChange={(e) => setConfirm(e.target.value)} />
          {confirm && confirm !== next && <p className="ui-hint" data-error="">{t("The two passwords differ")}</p>}
        </div>
        <label className="profile-password-show">
          <input type="checkbox" checked={show} onChange={(e) => setShow(e.target.checked)} /> {t("Show passwords")}
        </label>
        {error.message && !error.field && <p className="ui-hint" data-error="">{error.message}</p>}
        <button type="submit" className="ui-btn" disabled={!valid || saving}>
          <i className="ri-lock-password-line" aria-hidden="true"></i>
          {saving ? t("Saving…") : t("Save the new password")}
        </button>
      </form>
    </section>
  );
};

export default ChangePasswordCard;
